import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { updateAssetReferral } from "services/assetRecoveryService";

interface AddInvestigationDecisionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  referral?: any;
  onSuccess?: () => void;
  setReferralData?: React.Dispatch<React.SetStateAction<any | null>>;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

export const AddInvestigationDecisionPopup: React.FC<
  AddInvestigationDecisionPopupProps
> = ({
  isOpen,
  onClose,
  referralId,
  referral,
  onSuccess,
  setReferralData,
  setActiveTab,
}) => {
  const [formData, setFormData] = useState({
    prosecutor_decision_choice: "",
    prosecutor_decsion_summary: "",
    application_type: "",
    administrative_decision_summary: "",
    administrative_decision_date: "",
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [adminUploadedFiles, setAdminUploadedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();

  // Reset form when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        prosecutor_decision_choice: "",
        prosecutor_decsion_summary: "",
        application_type: "",
        administrative_decision_summary: "",
        administrative_decision_date: "",
      });
      setUploadedFiles([]);
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.prosecutor_decision_choice) {
      return "Please select a decision";
    }

    if (!formData.prosecutor_decsion_summary?.trim()) {
      return "Please provide a decision summary";
    }

    // Validate asset recovery type if proceeding to asset recovery
    if (formData.prosecutor_decision_choice === "proceed_to_asset_recovery") {
      if (!formData.application_type) {
        return "Please select an asset recovery type";
      }

      // Validate administrative fields if administrative type is selected
      if (formData.application_type === "administrative") {
        if (!formData.administrative_decision_summary?.trim()) {
          return "Please provide administrative decision summary";
        }
        if (!formData.administrative_decision_date) {
          return "Please select administrative decision date";
        }
      }
    }

    // Validate documents for do_not_proceed
    if (
      formData.prosecutor_decision_choice === "do_not_proceed" &&
      uploadedFiles.length === 0
    ) {
      return "Please upload at least one document for 'Do Not Proceed' decision";
    }

    return "";
  };

  const handleClose = () => {
    setFormData({
      prosecutor_decision_choice: "",
      prosecutor_decsion_summary: "",
      application_type: "",
      administrative_decision_summary: "",
      administrative_decision_date: "",
    });
    setUploadedFiles([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    setLoading(true);

    try {
      const submitFormData = new FormData();

      // Add decision fields
      submitFormData.append(
        "prosecutor_decision_choice",
        formData.prosecutor_decision_choice,
      );
      submitFormData.append(
        "prosecutor_decsion_summary",
        formData.prosecutor_decsion_summary,
      );

      // Add asset recovery type if proceeding to asset recovery
      if (formData.prosecutor_decision_choice === "proceed_to_asset_recovery") {
        // Add the asset recovery type
        submitFormData.append("application_type", formData.application_type);

        // Add administrative fields only if type is administrative
        if (formData.application_type === "administrative") {
          submitFormData.append(
            "administrative_decision_summary",
            formData.administrative_decision_summary || "",
          );

          if (formData.administrative_decision_date) {
            submitFormData.append(
              "administrative_decision_date",
              formData.administrative_decision_date,
            );
          }

          // Append administrative decision documents
          if (adminUploadedFiles.length > 0) {
            adminUploadedFiles.forEach((file) => {
              submitFormData.append("administrative_decision_documents", file);
            });
          }
        }
      }

      // Add document if decision is "do_not_proceed"
      if (
        formData.prosecutor_decision_choice === "do_not_proceed" &&
        uploadedFiles.length > 0
      ) {
        uploadedFiles.forEach((file) => {
          submitFormData.append(
            "prosecutor_decision_not_to_prosecute_document",
            file,
          );
        });
      }

      const response = await updateAssetReferral(referralId, submitFormData);

      if (response) {
        let successMessage = "";
        if (
          formData.prosecutor_decision_choice === "proceed_to_asset_recovery"
        ) {
          successMessage = `Decision submitted successfully! Proceeding to asset recovery (${formData.application_type} type)`;
          if (formData.application_type === "administrative") {
            successMessage += " with administrative details";
          }
        } else {
          successMessage =
            "Decision submitted successfully! Process completed.";
        }

        showTooltip(successMessage, "success");

        // Update referral data
        if (setReferralData) {
          setReferralData(response);
        }

        // Close popup and navigate accordingly
        handleClose();

        if (onSuccess) {
          onSuccess();
        }

        // Navigate based on decision
        if (setActiveTab) {
          if (
            formData.prosecutor_decision_choice === "proceed_to_asset_recovery"
          ) {
            setActiveTab("Investigation");
          } else {
            setActiveTab("Investigation");
          }
        }
      }
    } catch (error: any) {
      console.error("Error submitting prosecutor decision:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to submit decision";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Investigation Decision"
      description="Make a decision on the investigation"
      w="700px"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Decision Choice */}
        <div className="flex flex-col">
          <label className="referral-form-label mb-2">
            Decision <span className="text-red-500">*</span>
          </label>
          <select
            name="prosecutor_decision_choice"
            value={formData.prosecutor_decision_choice}
            onChange={handleInputChange}
            className="referral-form-field px-4 py-2.5"
            required
          >
            <option value="" disabled>
              Select a decision
            </option>
            <option value="proceed_to_asset_recovery">
              Proceed to Asset Recovery
            </option>
            <option value="do_not_proceed">Do Not Proceed</option>
          </select>
        </div>

        {/* Decision Summary */}
        <div className="flex flex-col">
          <label className="referral-form-label mb-2">
            Decision Summary <span className="text-red-500">*</span>
          </label>
          <textarea
            name="prosecutor_decsion_summary"
            value={formData.prosecutor_decsion_summary}
            onChange={handleInputChange}
            placeholder="Provide a detailed summary of your decision..."
            className="referral-form-field min-h-[120px] resize-vertical"
            required
          />
        </div>

        {/* Document Upload - Only show when "Do Not Proceed" is selected */}
        {formData.prosecutor_decision_choice === "do_not_proceed" && (
          <div className="flex flex-col mt-4">
            <label className="referral-form-label mb-2">
              Prosecutor Decision Not to Prosecute Document{" "}
              <span className="text-red-500">*</span>
            </label>
            <MultipleFileUploadComponent
              uploadedFiles={uploadedFiles}
              onFilesChange={setUploadedFiles}
              label="Upload Decision Document"
            />
            <p className="text-sm text-gray-500 mt-1">
              Please upload the official decision document
            </p>
          </div>
        )}

        {/* Decision Summary Card - Shows what happens with each choice */}
        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          {formData.prosecutor_decision_choice ===
          "proceed_to_asset_recovery" ? (
            <div className="mt-4 space-y-4">
              {/* Asset Recovery Type Selection */}
              <div className="space-y-3">
                <label className="referral-form-label mb-2">
                  {" "}
                  Asset Recovery Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="application_type"
                  value={formData.application_type || ""}
                  onChange={handleInputChange}
                  className="referral-form-field px-4 py-2.5"
                  required
                >
                  <option value="">Select recovery type</option>
                  <option value="administrative">Administrative Actions</option>
                  <option value="conventional">
                    Conventional Base Asset Recovery
                  </option>
                  <option value="non_conventional">
                    Non-Conventional Asset Recovery
                  </option>
                </select>
              </div>

              {/* Administrative Decision Fields - shown only when administrative is selected */}
              {formData.application_type === "administrative" && (
                <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-800">
                    Administrative Decision Details
                  </h4>

                  {/* Administrative Decision Summary */}
                  <div className="space-y-2">
                    <label className="referral-form-label mb-2">
                      {" "}
                      Administrative Decision Summary{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="administrative_decision_summary"
                      value={formData.administrative_decision_summary || ""}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter administrative decision summary..."
                      required
                    />
                  </div>

                  {/* Administrative Decision Date */}
                  <div className="space-y-2">
                    <label className="referral-form-label mb-2">
                      {" "}
                      Administrative Decision Date{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="administrative_decision_date"
                      value={formData.administrative_decision_date || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="flex flex-col mt-4">
                    <label className="referral-form-label mb-2">
                      Administrative Decision Document{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <MultipleFileUploadComponent
                      uploadedFiles={adminUploadedFiles}
                      onFilesChange={setAdminUploadedFiles}
                      label="Upload Decision Document"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : formData.prosecutor_decision_choice === "do_not_proceed" ? (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <svg
                className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span>
                The process will be <strong>completed</strong>. No further
                action will be taken on this referral. Please upload the
                required decision document.
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              Select a decision to see what happens next
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`flex-1 px-4 py-2.5 referral-form-button text-white disabled:opacity-50 disabled:cursor-not-allowed
              ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"}`}
          >
            {loading ? "Submitting..." : "Submit Decision"}
          </button>
        </div>
      </form>
    </BasePopup>
  );
};
